"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Calendar, User } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="h-12 w-3/4 rounded-lg bg-muted/30 animate-pulse mb-6" />
        <div className="h-96 w-full rounded-xl bg-muted/30 animate-pulse mb-8" />
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-6 rounded bg-muted/30 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12 text-center">
        <p className="text-muted-foreground">Failed to load post.</p>
      </div>
    );
  }

  const post = posts[0];

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12 text-center">
        <p className="text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-4xl px-6 py-12">
      <header className="mb-8">
        <h1 className="text-4xl font-display font-bold text-foreground mb-6 leading-tight">
          {post.title}
        </h1>

        <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="size-4" />
              <span>{post.author.name}</span>
            </div>
          )}
        </div>

        {post.featuredImage && (
          <div className="relative aspect-[21/9] w-full overflow-hidden rounded-xl">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="size-full object-cover"
            />
          </div>
        )}
      </header>

      {post.content && (
        <div
          className="prose prose-lg max-w-none mb-12 text-foreground"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      <Separator className="my-12" />

      {post.author && (
        <Card className="mb-12 border border-muted">
          <CardContent className="flex items-start gap-4">
            <Avatar className="size-16">
              {post.author.avatar?.["96"] && (
                <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                {post.author.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-display font-semibold text-lg text-foreground mb-1">
                About {post.author.name}
              </h3>
              <p className="text-sm text-muted-foreground">Author</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="mt-12">
        <h2 className="text-2xl font-display font-bold text-foreground mb-6">
          Related Posts
        </h2>
        <PostsProvider wp_query={{ per_page: 3, exclude: [post.id], order: "desc", orderby: "date" }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "date", "_embedded", "_links.wp:featuredmedia"]}>
          <RelatedPostsGrid />
        </PostsProvider>
      </div>
    </article>
  );
}

function RelatedPostsGrid() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-64 rounded-xl bg-muted/30 animate-pulse" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return <p className="text-muted-foreground text-sm">No related posts found.</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((relatedPost, i) => (
        <Card key={relatedPost.id} data-index={i} className="border border-muted hover-lift">
          {relatedPost.featuredImage && (
            <div className="relative aspect-video w-full overflow-hidden rounded-t-xl">
              <img
                src={relatedPost.featuredImage}
                alt={relatedPost.title}
                className="size-full object-cover"
              />
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-base font-display font-semibold text-foreground line-clamp-2">
              {relatedPost.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {relatedPost.excerpt && (
              <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                {relatedPost.excerpt.replace(/<[^>]*>/g, "")}
              </p>
            )}
            {relatedPost.link && (
              <Link to={relatedPost.link}>
                <Button variant="outline" size="sm" className="w-full">
                  Read More
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <section className="bg-background py-16">
      <PostsProvider wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}